import {
  IntegrationLogResponse,
  IntegrationLogStatusEnum,
} from '@monetize/types/app';
import { useIntegrationLogResync } from '../../hooks/useIntegrationLogResync';
import { MButton, MCenterModal, MGrid, MGridItem, MText } from '../Monetize';
import { BlankTextOrContent } from '../Monetize/BlankTextOrContent';
import { DateDisplay } from '../Monetize/DateDisplay';
import { IntegrationLogStatusTagStyle } from './constants';
import { ExternalRecordLinkConfig } from './getExternalRecordUrl';
import { SourceIdCell } from './SourceIdCell';
import { TargetIdCell } from './TargetIdCell';

const ItemLabel = ({ children }: { children: string }) => (
  <MText fontWeight="bold" mb="0.25rem" color="tPurple.base">
    {children}
  </MText>
);

const formatErrorResponse = (errorResponse: string) => {
  try {
    return JSON.stringify(JSON.parse(errorResponse), null, 2);
  } catch {
    return errorResponse;
  }
};

const TechnicalDetails = ({ errorResponse }: { errorResponse: string }) => (
  <MText
    as="pre"
    fontFamily="mono"
    fontSize="xs"
    whiteSpace="pre-wrap"
    overflowX="auto"
    maxHeight="400px"
    overflowY="auto"
    p={2}
    bg="bg.muted"
    borderRadius="md"
    borderWidth="1px"
    borderStyle="solid"
    borderColor="border.subtle"
    wordBreak="break-word"
  >
    {formatErrorResponse(errorResponse)}
  </MText>
);

interface IntegrationLogDetailModalProps {
  logItem?: IntegrationLogResponse;
  isOpen: boolean;
  isLoading?: boolean;
  onClose: () => void;
  /** Only Salesforce and HubSpot record URLs are constructible today — see getExternalRecordUrl. */
  externalRecordLinkConfig?: ExternalRecordLinkConfig;
}

/**
 * Presentational log-details modal. It renders whatever log it is handed, so it
 * works both from the `/settings/integration-log/:id/view` route (which fetches
 * by id) and from surfaces that already hold the log — e.g. the record-level
 * integration popover.
 */
export const IntegrationLogDetailModal = ({
  logItem,
  isOpen,
  isLoading,
  onClose,
  externalRecordLinkConfig = {},
}: IntegrationLogDetailModalProps) => {
  const { resyncLog, isLoading: isResyncing } = useIntegrationLogResync({
    closeDetailModal: onClose,
  });

  const isFailed = logItem?.status === IntegrationLogStatusEnum.FAILED;

  const handleResync = () => {
    if (logItem) {
      resyncLog(logItem);
    }
  };

  return (
    <MCenterModal
      isOpen={isOpen}
      isLoading={isLoading}
      onClose={onClose}
      modalTitle="Log Details"
      size="xl"
      modalHeaderProps={{ display: 'flex', alignItems: 'center' }}
      renderModalTitleActions={() =>
        logItem?.status && (
          <MText
            ml={2}
            fontSize="sm"
            fontWeight="normal"
            {...IntegrationLogStatusTagStyle[logItem.status]}
          >
            {logItem.status}
          </MText>
        )
      }
      renderFooter={() =>
        isFailed ? (
          <MButton
            variant="primary"
            onClick={handleResync}
            isDisabled={isResyncing}
            isLoading={isResyncing}
            data-testid="integration-log-retry-sync-btn"
          >
            Retry Sync
          </MButton>
        ) : null
      }
    >
      {logItem && (
        <MGrid
          templateColumns={{
            base: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          }}
          gap={4}
          // Record ids have no spaces to break on, so a long one (a Salesforce
          // 18-char id) pushes its column wide and scrolls the whole modal
          // sideways. `word-break` inherits, covering every value below.
          wordBreak="break-word"
        >
          <MGridItem>
            <ItemLabel>Timestamp</ItemLabel>
            <DateDisplay
              date={logItem.lastUpdatedAt}
              timezoneConfigKey="userTimezone"
            />
          </MGridItem>

          <MGridItem>
            <ItemLabel>Object</ItemLabel>
            <MText color="tPurple.base">{logItem.entityName}</MText>
          </MGridItem>

          <MGridItem>
            <ItemLabel>Operation</ItemLabel>
            <MText color="tPurple.base">{logItem.eventType}</MText>
          </MGridItem>

          <MGridItem>
            <ItemLabel>Label</ItemLabel>
            <BlankTextOrContent value={logItem.label}>
              <MText color="tPurple.base">{logItem.label}</MText>
            </BlankTextOrContent>
          </MGridItem>

          <MGridItem>
            <ItemLabel>Source Record ID</ItemLabel>
            <SourceIdCell sourceId={logItem.sourceId} />
          </MGridItem>

          <MGridItem>
            <ItemLabel>Target Record ID</ItemLabel>
            <BlankTextOrContent value={logItem.targetId}>
              <TargetIdCell
                targetId={logItem.targetId}
                targetSystemType={logItem.targetSystemType}
                entityName={logItem.entityName}
                linkConfig={externalRecordLinkConfig}
                textProps={{ color: 'tPurple.base' }}
              />
            </BlankTextOrContent>
          </MGridItem>

          <MGridItem colSpan={{ base: 1, lg: 3 }}>
            <ItemLabel>Error Message</ItemLabel>
            <BlankTextOrContent value={isFailed && logItem.log}>
              <MText color="tPurple.base" wordBreak="break-word">
                {logItem.log}
              </MText>
            </BlankTextOrContent>
          </MGridItem>

          {isFailed && logItem.errorResponse && (
            <MGridItem colSpan={{ base: 1, lg: 3 }}>
              <ItemLabel>Technical Details</ItemLabel>
              <TechnicalDetails errorResponse={logItem.errorResponse} />
            </MGridItem>
          )}
        </MGrid>
      )}
    </MCenterModal>
  );
};
